import api from './client'
import axios from 'axios'
import type { BulkDeleteResponse, BulkDownloadResponse, DocumentCategory, DocumentListResponse, DocumentPreviewResponse, DocumentRequestPayload, DocumentRequestResponse, DocumentRequestSummary, DocumentUploadPayload, DocumentUploadResponse, EmailDocumentPayload, GeneratedDocumentResponse, MerchantUploadInfo, PdfTemplateOption, ReturnUploadInfo, VoidDocumentRequestResponse } from '../api'

export const documentApi = {
  listForLead: async (leadId: string, search?: string): Promise<DocumentListResponse> => {
    const params = search ? { search } : undefined
    const response = await api.get<DocumentListResponse>(`/leads/${leadId}/documents`, { params })
    return response.data
  },

  listForMerchant: async (merchantId: string, search?: string): Promise<DocumentListResponse> => {
    const params = search ? { search } : undefined
    const response = await api.get<DocumentListResponse>(`/merchants/${merchantId}/documents`, { params })
    return response.data
  },

  upload: async (leadId: string, payload: DocumentUploadPayload): Promise<DocumentUploadResponse> => {
    const formData = new FormData()
    formData.append('file', payload.file)
    formData.append('label', payload.label)
    if (payload.is_restricted !== undefined) {
      formData.append('is_restricted', payload.is_restricted ? '1' : '0')
    }
    if (payload.category) {
      formData.append('category', payload.category)
    }
    const response = await api.post<DocumentUploadResponse>(`/leads/${leadId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  uploadForMerchant: async (merchantId: string, payload: DocumentUploadPayload): Promise<DocumentUploadResponse> => {
    const formData = new FormData()
    formData.append('file', payload.file)
    formData.append('label', payload.label)
    if (payload.is_restricted !== undefined) {
      formData.append('is_restricted', payload.is_restricted ? '1' : '0')
    }
    if (payload.category) {
      formData.append('category', payload.category)
    }
    const response = await api.post<DocumentUploadResponse>(`/merchants/${merchantId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  deleteDocument: async (documentId: string): Promise<void> => {
    await api.delete(`/documents/${documentId}`)
  },

  preview: async (documentId: string): Promise<DocumentPreviewResponse> => {
    const response = await api.get<DocumentPreviewResponse>(`/documents/${documentId}/preview`)
    return response.data
  },

  email: async (documentId: string, payload: EmailDocumentPayload): Promise<{ id: string; recipient_email: string; message: string }> => {
    const response = await api.post(`/documents/${documentId}/email`, payload)
    return response.data
  },

  bulkDelete: async (leadId: string, ids: string[]): Promise<BulkDeleteResponse> => {
    const response = await api.post<BulkDeleteResponse>(`/leads/${leadId}/documents/bulk-delete`, { ids })
    return response.data
  },

  bulkDeleteForMerchant: async (merchantId: string, ids: string[]): Promise<BulkDeleteResponse> => {
    const response = await api.post<BulkDeleteResponse>(`/merchants/${merchantId}/documents/bulk-delete`, { ids })
    return response.data
  },

  bulkDownload: async (leadId: string, ids: string[]): Promise<BulkDownloadResponse> => {
    const response = await api.post<BulkDownloadResponse>(`/leads/${leadId}/documents/bulk-download`, { ids })
    return response.data
  },

  bulkDownloadForMerchant: async (merchantId: string, ids: string[]): Promise<BulkDownloadResponse> => {
    const response = await api.post<BulkDownloadResponse>(`/merchants/${merchantId}/documents/bulk-download`, { ids })
    return response.data
  },

  pdfTemplates: async (leadId: string): Promise<{ templates: PdfTemplateOption[] }> => {
    const response = await api.get<{ templates: PdfTemplateOption[] }>(`/leads/${leadId}/pdf-templates`)
    return response.data
  },

  generatePdf: async (leadId: string, template_key: string, label?: string): Promise<GeneratedDocumentResponse> => {
    const response = await api.post<GeneratedDocumentResponse>(`/leads/${leadId}/generate-pdf`, {
      template_key,
      ...(label ? { label } : {}),
    })
    return response.data
  },

  requestDocuments: async (leadId: string, data: DocumentRequestPayload): Promise<DocumentRequestResponse> => {
    const response = await api.post<DocumentRequestResponse>(`/leads/${leadId}/document-requests`, data)
    return response.data
  },

  requestDocumentsForMerchant: async (merchantId: string, data: DocumentRequestPayload): Promise<DocumentRequestResponse> => {
    const response = await api.post<DocumentRequestResponse>(`/merchants/${merchantId}/document-requests`, data)
    return response.data
  },
}

export const merchantUploadApi = {
  getUploadInfo: async (token: string): Promise<MerchantUploadInfo> => {
    const response = await axios.get<MerchantUploadInfo>(`/api/document-requests/${token}/upload`)
    return response.data
  },

  upload: async (token: string, category: DocumentCategory, file: File): Promise<{ message: string; document_upload_id: string; remaining_categories: DocumentCategory[] }> => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('category', category)
    const response = await axios.post(`/api/document-requests/${token}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },
}

export const returnUploadApi = {
  getUploadInfo: async (token: string): Promise<ReturnUploadInfo> => {
    const response = await axios.get<ReturnUploadInfo>(`/api/upload/${token}`)
    return response.data
  },

  upload: async (token: string, file: File): Promise<{ message: string; document_upload_id: string }> => {
    const formData = new FormData()
    formData.append('file', file)
    const response = await axios.post(`/api/upload/${token}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },
}

export const documentRequestApi = {
  listForLead: async (leadId: string): Promise<{ document_requests: DocumentRequestSummary[] }> => {
    const response = await api.get<{ document_requests: DocumentRequestSummary[] }>(`/leads/${leadId}/document-requests`)
    return response.data
  },

  listForMerchant: async (merchantId: string): Promise<{ document_requests: DocumentRequestSummary[] }> => {
    const response = await api.get<{ document_requests: DocumentRequestSummary[] }>(`/merchants/${merchantId}/document-requests`)
    return response.data
  },

  void: async (documentRequestId: string): Promise<VoidDocumentRequestResponse> => {
    const response = await api.post<VoidDocumentRequestResponse>(`/document-requests/${documentRequestId}/void`)
    return response.data
  },
}
